import React, { useRef, useState, useEffect } from 'react';
import { PixelGlyph } from './PixelGlyph';

interface MagneticButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary' | 'sunset' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showArrow?: boolean;
  target?: string;
  rel?: string;
}

export const MagneticButton: React.FC<MagneticButtonProps> = ({
  children,
  onClick,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  showArrow = true,
  target,
  rel,
}) => {
  const btnRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isFinePointer, setIsFinePointer] = useState(false);

  useEffect(() => {
    // Only enable magnetic physics on devices with a mouse/trackpad (never touchscreens)
    const mq = window.matchMedia('(pointer: fine)');
    setIsFinePointer(mq.matches);

    const handleMqChange = (e: MediaQueryListEvent) => setIsFinePointer(e.matches);
    mq.addEventListener('change', handleMqChange);

    // If page scrolls, immediately spring button back to prevent stuck offsets
    const handleScroll = () => {
      setPosition((prev) => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 }));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mq.removeEventListener('change', handleMqChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isFinePointer || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const pullFactor = 0.28;
    const deltaX = (e.clientX - centerX) * pullFactor;
    const deltaY = (e.clientY - centerY) * pullFactor;

    setPosition({ x: deltaX, y: deltaY });
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 });
  };

  const handleClick = () => {
    resetPosition();
    if (onClick) onClick();
  };

  // Variants styling
  let baseStyles = 'relative inline-flex items-center justify-between rounded-full font-sans font-medium transition-all duration-300 group overflow-hidden select-none cursor-pointer';
  let sizeStyles = size === 'sm' ? 'px-4 py-2 text-xs gap-2' : size === 'lg' ? 'px-8 py-4 text-base gap-4' : 'px-6 py-3 text-sm gap-3';
  let variantStyles = '';

  switch (variant) {
    case 'primary':
      variantStyles = 'bg-cream-100 text-espresso-950 hover:bg-white shadow-md active:scale-[0.98]';
      break;
    case 'sunset':
      variantStyles = 'bg-gradient-to-r from-sunset-gold via-sunset-flame to-sunset-crimson text-white hover:brightness-110 shadow-vermilion-glow active:scale-[0.98]';
      break;
    case 'secondary':
      variantStyles = 'bg-espresso-850 text-cream-100 border border-white/10 hover:border-sunset-gold/50 hover:bg-espresso-800 shadow-bezel-outer active:scale-[0.98]';
      break;
    case 'ghost':
      variantStyles = 'bg-transparent text-cream-200 hover:text-white hover:bg-white/5 active:scale-[0.98]';
      break;
  }

  const iconCircleStyle = variant === 'primary'
    ? 'bg-espresso-950/10 text-espresso-950'
    : variant === 'sunset'
    ? 'bg-white/20 text-white'
    : 'bg-white/10 text-sunset-gold';

  const content = (
    <>
      <span className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5">
        {children}
      </span>
      {showArrow && (
        <span
          className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${iconCircleStyle}`}
        >
          <PixelGlyph name="arrow-up-right" size={14} />
        </span>
      )}
    </>
  );

  const style = isFinePointer && (position.x !== 0 || position.y !== 0)
    ? {
        transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
        transition: 'transform 0.15s ease-out',
      }
    : {
        transform: 'translate3d(0, 0, 0)',
        transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
      };

  if (href) {
    return (
      <a
        ref={btnRef}
        href={href}
        target={target}
        rel={rel}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={resetPosition}
        onTouchEnd={resetPosition}
        onTouchCancel={resetPosition}
        onBlur={resetPosition}
        style={style}
        className={`${baseStyles} ${sizeStyles} ${variantStyles} ${className}`}
        data-cursor="interactive"
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={resetPosition}
      onTouchEnd={resetPosition}
      onTouchCancel={resetPosition}
      onBlur={resetPosition}
      style={style}
      className={`${baseStyles} ${sizeStyles} ${variantStyles} ${className}`}
      data-cursor="interactive"
    >
      {content}
    </button>
  );
};
